import DatabaseModel from './DatabaseModel.js';
import { unwrapDto } from './responseUtils.js';
import { atarALaSesion, comoPromesaComun, generacionActual } from '../utils/sesionDePeticiones.js';

// #1044 · Microsoft 365 desde la ficha del empleado y sincronización de empleados existentes.
// Las peticiones van sin caché (`cached=false`): es el estado de una solicitud que avanza en el servidor, y una
// foto vieja diría «pendiente» de algo que ya terminó, o al revés.
//
// El navegador no manda nunca grupos, tenant, SKU ni roles: solo datos de la tarjeta y la cuenta
// que el responsable ha visto y confirma. El modo (simulado, real o apagado) lo decide el servidor.
const Microsoft365 = Object.create(DatabaseModel);

Microsoft365.model = 'microsoft365';

Microsoft365.url = function(ruta) {
    return this.server_movilidad + this.prefix + this.model + ruta;
};

// Ni este cliente ni `sendRequest` tenían tope, y una petición sin respuesta dejaba la pantalla
// esperando para siempre con los botones apagados. Pasado el tope, toda petición de aquí rechaza con
// `sinRespuesta` y lo que conteste después ya no le llega a nadie. Es más largo que los de la tarjeta,
// que avisan antes con su propio texto: este es el suelo para todo lo que pase por aquí.
export const ESPERA_MAX_MS = 60000;
export const SIN_RESPUESTA = 'El servidor no ha contestado a tiempo.';

/**
 * Pasado el tope, además de darla por perdida, se CANCELA en el navegador. Darla solo por perdida
 * dejaba la petición viva: la tarjeta, con una solicitud en curso, vuelve a leer a los 8 s de cada
 * fallo, y cada lectura abandonada seguía ocupando su sitio en la cola del navegador, que sobre
 * HTTP/1.1 tiene seis conexiones por servidor para TODA la aplicación. Cuando esa cola se atasca
 * —lo que pasó en la preview del 30/10/2026: la tarjeta, Empleados/Nuevo y Logs a la vez—, las
 * abandonadas se sumaban a la cola y la alargaban. `abort()` no dispara el `error` de Framework7, así
 * que cancelarla no saca otro aviso: el que se ve es el de no haber contestado a tiempo.
 *
 * @param {Number} [espera] milisegundos; quien ya avisa antes con su propio texto (la tarjeta) pasa
 *        el suyo para que la petición no le sobreviva. Por defecto, `ESPERA_MAX_MS`.
 */
Microsoft365.pedir = function(metodo, ruta, datos, espera) {
    const gen = generacionActual();
    let xhr = null;
    const params = { url: this.url(ruta), method: metodo, beforeSend: function(x) { xhr = x; } };
    if (datos !== undefined) {
        params.data = datos;
    }
    let vigia;
    const tope = new Promise(function(ok, mal) {
        vigia = setTimeout(function() {
            if (xhr) xhr.abort();
            mal({ sinRespuesta: true, status: null, message: SIN_RESPUESTA });
        }, espera || ESPERA_MAX_MS);
    });
    const respuesta = comoPromesaComun(this.sendRequest(params, false)).then(unwrapDto);
    const primera = Promise.race([respuesta, tope]).then(
        function(r) { clearTimeout(vigia); return r; },
        function(e) { clearTimeout(vigia); throw e; }
    );
    // Atada a la sesión que la lanzó, como la de `sendRequest`: el vencimiento tampoco corre en la siguiente.
    return atarALaSesion(primera, gen);
};

// GET → { modo, modoPedido, motivoModo, puedeGestionar, rolesGestion[], dominio, destinos[], licencia,
//         grupoLicencia, equipo, cuentasFicticias[] }
Microsoft365.estado = function() {
    return this.pedir('GET', '/estado');
};

// GET → tarjeta: datos guardados y sugeridos, vínculo, solicitud, pasos[], acciones{ disponible, motivo }
Microsoft365.ficha = function(empleadoId, espera) {
    return this.pedir('GET', '/empleado/' + encodeURIComponent(empleadoId), undefined, espera);
};

// PUT { perfil, correo, nombre, apellidos, ubicacionUso } → tarjeta
Microsoft365.guardarDatos = function(empleadoId, datos, espera) {
    return this.pedir('PUT', '/empleado/' + encodeURIComponent(empleadoId) + '/datos', datos, espera);
};

// POST → tarjeta con la solicitud PENDIENTE. Encolada no es hecha: el panel lo dice paso a paso.
Microsoft365.solicitarAlta = function(empleadoId, espera) {
    return this.pedir('POST', '/empleado/' + encodeURIComponent(empleadoId) + '/alta', {}, espera);
};

// GET → { correo, estado, entraObjectId, upn, nombre, tipo, licencia, conflictos[], vinculable }. Solo lee.
Microsoft365.correspondencia = function(empleadoId, espera) {
    return this.pedir('GET', '/empleado/' + encodeURIComponent(empleadoId) + '/correspondencia', undefined, espera);
};

// POST { entraObjectId } → tarjeta. El servidor vuelve a buscar y solo vincula si sale esa misma cuenta.
Microsoft365.vincular = function(empleadoId, entraObjectId, espera) {
    return this.pedir('POST', '/empleado/' + encodeURIComponent(empleadoId) + '/vincular', { entraObjectId: entraObjectId }, espera);
};

Microsoft365.reintentar = function(empleadoId, espera) {
    return this.pedir('POST', '/empleado/' + encodeURIComponent(empleadoId) + '/reintentar', {}, espera);
};

Microsoft365.confirmarArchivo = function(empleadoId, espera) {
    return this.pedir('POST', '/empleado/' + encodeURIComponent(empleadoId) + '/archivo-confirmado', {}, espera);
};

// POST → { codigo, caducaEn, unSoloUso, aviso }. Se enseña una vez y no se guarda en ningún sitio.
Microsoft365.accesoTemporal = function(empleadoId, espera) {
    return this.pedir('POST', '/empleado/' + encodeURIComponent(empleadoId) + '/acceso-temporal', {}, espera);
};

// GET → { consultado, equipos }
Microsoft365.equipos = function(empleadoId, espera) {
    return this.pedir('GET', '/empleado/' + encodeURIComponent(empleadoId) + '/equipos', undefined, espera);
};

// Sincronización de empleados existentes: diagnóstico (solo lee) y ejecución explícita.
Microsoft365.prepararDiagnostico = function() {
    return this.pedir('POST', '/sincronizacion/diagnostico', {});
};

Microsoft365.ultimaSincronizacion = function() {
    return this.pedir('GET', '/sincronizacion/ultima');
};

Microsoft365.sincronizacion = function(loteId) {
    return this.pedir('GET', '/sincronizacion/' + encodeURIComponent(loteId));
};

Microsoft365.ejecutar = function(loteId, empleadoIds) {
    return this.pedir('POST', '/sincronizacion/' + encodeURIComponent(loteId) + '/ejecutar', { empleadoIds: empleadoIds });
};

export default Microsoft365;
